import type { AlertEvaluation, AlertItem } from '@shared/alerts';
import { ALERT_LEVEL_LABELS } from '@shared/alerts';
import { Badge } from '../../../components/Badge';
import type { LinkSnapshot } from '../../../drone/session';

interface AlertBannerProps {
  evaluation: AlertEvaluation | null;
  link: LinkSnapshot | null;
  /** 距最后一帧的毫秒数，用于离线判定提示 */
  frameAgeMs: number | null;
  staleAfterMs: number;
  className?: string;
}

const LEVEL_STYLES = {
  nominal: {
    box: 'border-ok/60 bg-ok-soft',
    title: 'text-fg',
    accent: 'var(--ok)',
  },
  caution: {
    box: 'border-caution/70 bg-caution-soft',
    title: 'text-fg',
    accent: 'var(--caution)',
  },
  warning: {
    box: 'border-warning bg-warning-soft',
    title: 'text-fg',
    accent: 'var(--warning)',
  },
  critical: {
    box: 'border-critical bg-critical-soft',
    title: 'text-fg',
    accent: 'var(--critical)',
  },
} as const;

/**
 * 顶部告警条。
 *
 * 单机管理页不允许出现弹窗蒙版，因此所有告警都必须以「常驻、醒目、可读」的
 * 条幅形式呈现：严重告警使用实心边框与加粗大字，并同时给出当前值与阈值。
 */
export function AlertBanner({
  evaluation,
  link,
  frameAgeMs,
  staleAfterMs,
  className = '',
}: AlertBannerProps) {
  const level = evaluation?.level ?? 'nominal';
  const items = evaluation?.items ?? [];
  const offline = frameAgeMs === null || frameAgeMs > staleAfterMs;
  const style = LEVEL_STYLES[level];

  const linkProblem = link && link.phase !== 'streaming' ? link.message : null;

  return (
    <div className={['flex flex-col gap-2', className].join(' ')} role="status" aria-live="polite">
      <div
        className={['flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border-2 px-3 py-2', style.box].join(
          ' ',
        )}
      >
        <span
          aria-hidden
          className={[
            'size-3 shrink-0 rounded-full',
            level === 'critical' ? 'animate-pulse' : '',
          ].join(' ')}
          style={{ background: style.accent }}
        />
        <span className={['text-base font-bold tracking-wide', style.title].join(' ')}>
          {/* 正常等级不能叫「正常告警」——没有告警可言，直接说状态正常 */}
          {offline ? '数据中断' : level === 'nominal' ? '状态正常' : `${ALERT_LEVEL_LABELS[level]}告警`}
        </span>

        {offline ? (
          <span className="text-sm font-semibold text-fg">
            {frameAgeMs === null
              ? `尚未收到任何遥测帧（阈值 ${Math.round(staleAfterMs / 1000)} 秒），请立即确认飞行器在线状态与接入配置`
              : `已 ${Math.max(0, Math.round(frameAgeMs / 1000))} 秒未收到遥测帧（阈值 ${Math.round(
                  staleAfterMs / 1000,
                )} 秒），请立即确认飞行器状态`}
          </span>
        ) : items.length === 0 ? (
          <span className="text-sm text-fg">所有关键参数均在安全阈值内</span>
        ) : (
          <span className="text-sm text-fg">
            共 {items.length} 项参数越过阈值，最高等级为「{ALERT_LEVEL_LABELS[level]}」
          </span>
        )}

        <div className="ml-auto flex items-center gap-1.5">
          {link ? <Badge tone="neutral">链路：{LINK_PHASE_LABELS[link.phase]}</Badge> : null}
          {link?.transport ? (
            <Badge tone="neutral">{link.transport === 'ws' ? 'WebSocket 推送' : 'REST 轮询'}</Badge>
          ) : null}
          {link && link.droppedFrames > 0 ? (
            <Badge tone="caution">丢帧 {link.droppedFrames}</Badge>
          ) : null}
        </div>
      </div>

      {items.length > 0 ? (
        <ul className="grid grid-cols-1 gap-1.5 lg:grid-cols-2 xl:grid-cols-3">
          {items.map((item) => (
            <AlertRow key={item.id} item={item} />
          ))}
        </ul>
      ) : null}

      {linkProblem ? (
        <p className="rounded border border-line bg-surface-2 px-3 py-1.5 text-xs text-fg-muted">
          链路提示：{linkProblem}
          {link && link.retryAttempt > 0 ? `（第 ${link.retryAttempt} 次重试）` : ''}
        </p>
      ) : null}
    </div>
  );
}

function AlertRow({ item }: { item: AlertItem }) {
  const critical = item.level === 'critical';
  const warning = item.level === 'warning';
  return (
    <li
      className={[
        'flex items-start gap-2 rounded border px-2.5 py-1.5',
        critical
          ? 'border-critical bg-critical-soft'
          : warning
            ? 'border-warning bg-warning-soft'
            : 'border-caution bg-caution-soft',
      ].join(' ')}
    >
      <span
        aria-hidden
        className="mt-1.5 size-2 shrink-0 rounded-full"
        style={{
          background: critical ? 'var(--critical)' : warning ? 'var(--warning)' : 'var(--caution)',
        }}
      />
      <div className="flex min-w-0 flex-col">
        <span className="text-xs font-bold text-fg">
          {item.label}
          <span className="ml-1.5 font-normal text-fg-muted">[{ALERT_LEVEL_LABELS[item.level]}]</span>
        </span>
        <span className="text-xs leading-snug text-fg">{item.message}</span>
        <span className="tabular mt-0.5 text-[11px] text-fg-muted">
          当前 {item.value}
          {item.unit} · 阈值 {item.threshold === null ? '无' : `${item.threshold}${item.unit}`}
        </span>
      </div>
    </li>
  );
}

export const LINK_PHASE_LABELS: Record<LinkSnapshot['phase'], string> = {
  idle: '未连接',
  connecting: '连接中',
  streaming: '实时推送',
  polling: '轮询中',
  retrying: '重连中',
  blocked: '已阻断',
  stopped: '已停止',
};
